export type ScreenPoint = { x: number; y: number }

export type ScreenCandidate<T> = T & ScreenPoint

export type HitTestFeature = {
  properties?: Record<string, unknown> | null
  geometry?: { type: string; coordinates?: unknown } | null
}

export type AddModeNodeDecision = 'appendContinuation' | 'acceptHint' | 'setAddTarget' | 'none'

export function pickClosestFeatureByScreenDistance<T extends ScreenPoint>(
  click: ScreenPoint,
  candidates: T[]
): T | null {
  if (candidates.length === 0) return null
  let best = candidates[0]!
  let bestDist = squaredDistance(click, best)
  for (let i = 1; i < candidates.length; i++) {
    const candidate = candidates[i]!
    const dist = squaredDistance(click, candidate)
    if (dist < bestDist) {
      best = candidate
      bestDist = dist
    }
  }
  return best
}

export function readPropertyId(value: unknown): string | null {
  if (typeof value === 'string' && value.length > 0) return value
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return null
}

/**
 * Among overlapping node hits from queryRenderedFeatures, pick the node whose
 * projected Point geometry is nearest the click. Array order is not reliable.
 */
export function pickClosestNodeId(
  click: ScreenPoint,
  features: HitTestFeature[],
  project: (lngLat: [number, number]) => ScreenPoint
): string | null {
  const byNodeId = new Map<string, ScreenCandidate<{ nodeId: string }>>()

  for (const feature of features) {
    const nodeId = readPropertyId(feature.properties?.nodeId)
    if (!nodeId) continue
    if (feature.geometry?.type !== 'Point') continue
    const coords = feature.geometry.coordinates
    if (!Array.isArray(coords) || coords.length < 2) continue
    const lng = coords[0]
    const lat = coords[1]
    if (typeof lng !== 'number' || typeof lat !== 'number') continue
    const screen = project([lng, lat])
    const existing = byNodeId.get(nodeId)
    if (!existing || squaredDistance(click, screen) < squaredDistance(click, existing)) {
      byNodeId.set(nodeId, { nodeId, x: screen.x, y: screen.y })
    }
  }

  const closest = pickClosestFeatureByScreenDistance(click, [...byNodeId.values()])
  return closest?.nodeId ?? null
}

/**
 * Immediate continuation neighbors must win over accepting a pending Dijkstra hint
 * (e.g. leftover addTarget on a different nearby node).
 */
export function decideAddModeNodeClick(opts: {
  hasContinuationMatch: boolean
  shouldAcceptHintOnThisNode: boolean
  canSetAddTarget: boolean
}): AddModeNodeDecision {
  if (opts.hasContinuationMatch) return 'appendContinuation'
  if (opts.shouldAcceptHintOnThisNode) return 'acceptHint'
  if (opts.canSetAddTarget) return 'setAddTarget'
  return 'none'
}

function squaredDistance(a: ScreenPoint, b: ScreenPoint): number {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return dx * dx + dy * dy
}
